import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Field, NativeSelect, Empty } from "./common";
import { api, errMsg, useFetch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";

export function FormFields({ fields, f, setF }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields.map((x) => (
        <Field key={x.key} label={x.label} required={x.required} className={x.wide ? "sm:col-span-2" : ""}>
          {x.type === "select" ? (
            <NativeSelect testid={`field-${x.key}`} value={f[x.key]} placeholder={x.placeholder ?? "—"} onChange={(v) => setF({ ...f, [x.key]: v })} options={x.options} />
          ) : (
            <Input data-testid={`field-${x.key}`} type={x.type || "text"} step="any" value={f[x.key] ?? ""}
              onChange={(e) => setF({ ...f, [x.key]: x.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value })} />
          )}
        </Field>
      ))}
    </div>
  );
}

export default function SimpleCrud({ endpoint, columns, fields, title, newLabel, defaults = {}, testid, writePerm = "master:write" }) {
  const { t } = useT();
  const { can } = useAuth();
  const { data, reload } = useFetch(endpoint);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState({});
  const open = (row) => { setEdit(row || {}); setF(row ? { ...row } : { ...defaults }); };
  const save = async () => {
    const miss = fields.filter((x) => x.required && (f[x.key] == null || f[x.key] === ""));
    if (miss.length) return toast.error(`${t("common.required")}: ${miss.map((x) => x.label).join(", ")}`);
    try {
      const base = endpoint.split("?")[0];
      if (edit.id) await api.put(`${base}/${edit.id}`, f); else await api.post(base, f);
      toast.success(t("common.saved")); setEdit(null); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (row) => {
    if (!window.confirm(t("common.delete") + "?")) return;
    try { await api.delete(`${endpoint.split("?")[0]}/${row.id}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const canWrite = can(writePerm);
  return (
    <div data-testid={testid}>
      <div className="mb-3 flex items-center justify-between">
        {title && <h3 className="text-sm font-bold uppercase tracking-wider">{title}</h3>}
        {canWrite && <Button size="sm" data-testid={`${testid}-new-btn`} onClick={() => open(null)}><Plus className="h-4 w-4" />{newLabel || t("common.new")}</Button>}
      </div>
      <div className="overflow-x-auto rounded-md border border-neutral-200 bg-white">
        <table className="dense-table w-full">
          <thead><tr>{columns.map((c) => <th key={c.key} className={c.num ? "text-right" : ""}>{c.label}</th>)}{canWrite && <th />}</tr></thead>
          <tbody>
            {(data || []).map((r) => (
              <tr key={r.id} data-testid={`${testid}-row`}>
                {columns.map((c) => <td key={c.key} className={c.num ? "num" : ""}>{c.render ? c.render(r) : r[c.key]}</td>)}
                {canWrite && <td className="whitespace-nowrap text-right">
                  <Button size="sm" variant="outline" data-testid={`${testid}-edit-${r.id}`} onClick={() => open(r)}><Pencil className="h-3.5 w-3.5" />{t("common.edit")}</Button>
                  <button className="ml-1 p-1 text-neutral-400 hover:text-[#db0720]" data-testid={`${testid}-delete-${r.id}`} onClick={() => del(r)}><Trash2 className="h-3.5 w-3.5" /></button>
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
        {data && !data.length && <Empty>{t("common.empty")}</Empty>}
      </div>
      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle>{edit?.id ? t("common.edit") : newLabel || t("common.new")}</DialogTitle></DialogHeader>
          <FormFields fields={fields} f={f} setF={setF} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>{t("common.cancel")}</Button>
            <Button data-testid={`${testid}-save-btn`} onClick={save}>{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
